// frontend/src/components/dialogs/SegmentVariantSelector.jsx
// Component for selecting segments and naming variants when creating segment variants
// from completed trips. See docs/2026-01-05-SEGMENT_VARIANT_CREATION.md

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import {
    AlertTriangle, Check, Info, AlertCircle,
    Loader2, ChevronDown, ChevronRight, Clock, MapPin, Merge, Split, X
} from 'lucide-react';
import { toast } from 'react-toastify';
import { isValidVariantName, sanitizeVariantName } from '../../utils/variantNaming';
import ConfirmDialogShell from './ConfirmDialogShell';
import segmentMatrixService from '../../services/segmentMatrixService';
import { useDateTimePreferences } from '../../hooks/useDateTimePreferences';
import {
    buildSegmentKey,
    formatDistanceMeters,
    formatDurationSeconds
} from '../../utils/segmentUtils';
import { getZeroDistanceLabel } from '../../utils/distanceUtils';

const getPositiveDurationSeconds = (value) => {
    const seconds = Number(value);
    return Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : null;
};

const getSegmentDisplayDurationSeconds = (segment) => (
    getPositiveDurationSeconds(segment.reference_duration_seconds)
    ?? getPositiveDurationSeconds(segment.referenceDurationSeconds)
    ?? getPositiveDurationSeconds(segment.referenceDuration)
    ?? getPositiveDurationSeconds(segment.refDuration)
    ?? getPositiveDurationSeconds(segment.tripDuration)
    ?? getPositiveDurationSeconds(segment.ors_duration_seconds)
    ?? getPositiveDurationSeconds(segment.orsDurationSeconds)
    ?? getPositiveDurationSeconds(segment.orsDuration)
);

/**
 * Deviation status icons and colors
 */
const getDeviationDisplay = (deviation, t) => {
    if (!deviation) {
        return {
            icon: <Info className="w-4 h-4 text-gray-400" />,
            label: t('noReferenceAvailable'),
            bgColor: 'bg-gray-50 dark:bg-gray-800/50',
            textColor: 'text-gray-600 dark:text-gray-400',
            borderColor: 'border-gray-200 dark:border-gray-700'
        };
    }

    switch (deviation.status) {
        case 'different':
            return {
                icon: <AlertTriangle className="w-4 h-4 text-orange-500" />,
                label: deviation.statusLabel || t('differentRouteDetected'),
                bgColor: 'bg-orange-50 dark:bg-orange-900/20',
                textColor: 'text-orange-700 dark:text-orange-400',
                borderColor: 'border-orange-200 dark:border-orange-700'
            };
        case 'minor':
            return {
                icon: <AlertCircle className="w-4 h-4 text-yellow-500" />,
                label: deviation.statusLabel || t('minorDeviation'),
                bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
                textColor: 'text-yellow-700 dark:text-yellow-400',
                borderColor: 'border-yellow-200 dark:border-yellow-700'
            };
        case 'similar':
            return {
                icon: <Check className="w-4 h-4 text-green-500" />,
                label: deviation.statusLabel || t('similarToReference'),
                bgColor: 'bg-green-50 dark:bg-green-900/20',
                textColor: 'text-green-700 dark:text-green-400',
                borderColor: 'border-green-200 dark:border-green-700'
            };
        case 'no_reference':
        case 'insufficient_data':
        default:
            return {
                icon: <Info className="w-4 h-4 text-gray-400" />,
                label: deviation.statusLabel || t('noReferenceAvailable'),
                bgColor: 'bg-gray-50 dark:bg-gray-800/50',
                textColor: 'text-gray-600 dark:text-gray-400',
                borderColor: 'border-gray-200 dark:border-gray-700'
            };
    }
};

/**
 * Single segment row component
 */
const SegmentRow = ({
    segment,
    isSelected,
    variantName,
    onToggle,
    onVariantNameChange,
    existingVariants,
    disabled,
    onDeleteExistingVariant,
    t,
    cn,
    text,
    border,
    background,
    distanceUnit,
    durationLanguage
}) => {
    const deviationDisplay = getDeviationDisplay(segment.deviation, t);
    const [isManuallyExpanded, setIsManuallyExpanded] = useState(isSelected);
    const zeroDistanceLabel = getZeroDistanceLabel(distanceUnit, t);
    const displayDurationSeconds = getSegmentDisplayDurationSeconds(segment);

    useEffect(() => {
        setIsManuallyExpanded(isSelected);
    }, [isSelected]);

    const isExpanded = isSelected && isManuallyExpanded;

    return (
        <div className={cn(
            "border rounded-lg transition-all duration-200",
            deviationDisplay.borderColor,
            isSelected ? 'ring-2 ring-blue-500' : ''
        )}>
            {/* Header row */}
            <div
                className={cn(
                    "flex items-center gap-3 p-3 cursor-pointer",
                    deviationDisplay.bgColor
                )}
                onClick={() => !disabled && onToggle()}
            >
                {/* Checkbox — click is handled by parent row div, so make non-interactive to avoid double-toggle */}
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    disabled={disabled}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 pointer-events-none"
                />

                {/* Segment info */}
                <div className="flex-1 min-w-0">
                    <div className={cn("font-medium flex items-center gap-2 flex-wrap", text.primary)}>
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span title={segment.fromName}>{segment.fromName}</span>
                        <span className="text-gray-400">→</span>
                        <span title={segment.toName}>{segment.toName}</span>
                    </div>

                    {/* Deviation status */}
                    <div className={cn("flex items-center gap-2 mt-1 text-sm", deviationDisplay.textColor)}>
                        {deviationDisplay.icon}
                        <span>{deviationDisplay.label}</span>
                        {segment.deviation?.avgDeviationM != null && (
                            <span className="text-xs">
                                ({t('avgDeviation')}: {formatDistanceMeters(segment.deviation.avgDeviationM, distanceUnit, t, { compact: false, zeroLabel: zeroDistanceLabel })})
                            </span>
                        )}
                    </div>
                </div>

                {/* Metrics */}
                <div className="flex items-center gap-4 text-sm">
                    {displayDurationSeconds != null && (
                        <div className={cn("flex items-center gap-1", text.secondary)}>
                            <Clock className="w-3 h-3" />
                            <span>{formatDurationSeconds(displayDurationSeconds, t, { language: durationLanguage })}</span>
                        </div>
                    )}
                    {segment.refDistance != null && (
                        <div className={cn("flex items-center gap-1", text.secondary)}>
                            <span>{formatDistanceMeters(segment.refDistance, distanceUnit, t, { compact: false, zeroLabel: zeroDistanceLabel })}</span>
                        </div>
                    )}
                </div>

                {/* Expand indicator */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsManuallyExpanded((prev) => !prev);
                    }}
                    disabled={disabled}
                    data-testid={`segment-row-toggle-${segment.fromWaypointId}-${segment.toWaypointId}`}
                    className={cn("p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700", text.secondary)}
                >
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
            </div>

            {/* Expanded content - variant name input */}
            {isExpanded && isSelected && (
                <div className={cn("p-3 border-t", border.default, background.card)}>
                    <div className="flex items-center gap-3">
                        <label className={cn("text-sm font-medium whitespace-nowrap", text.primary)}>
                            {t('variantName')}:
                        </label>
                        <input
                            type="text"
                            value={variantName}
                            onChange={(e) => onVariantNameChange(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                            placeholder={t('variantNamePlaceholder')}
                            className={cn(
                                "flex-1 px-3 py-1.5 text-sm rounded border",
                                border.input,
                                background.input,
                                text.primary
                            )}
                            disabled={disabled}
                            maxLength={30}
                        />
                    </div>

                    {/* Existing variants info */}
                    {existingVariants && existingVariants.length > 0 && (
                        <div className={cn("mt-2 text-xs", text.secondary)}>
                            <div className="mb-1">{t('existingVariants')}:</div>
                            <div className="flex flex-wrap gap-2">
                                {existingVariants.map(existingVariant => (
                                    <span
                                        key={existingVariant}
                                        className={cn(
                                            "inline-flex items-center gap-1 rounded-full border px-2 py-1",
                                            border.default,
                                            background.secondary,
                                            text.secondary
                                        )}
                                    >
                                        <span>{existingVariant}</span>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDeleteExistingVariant(existingVariant);
                                            }}
                                            disabled={disabled}
                                            className="text-red-500 hover:text-red-600"
                                            aria-label={t('delete')}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Validation error */}
                    {variantName === 'standard' && (
                        <div className="mt-2 text-xs text-red-500">
                            {t('standardReserved')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * Main component for segment variant selection
 */
export const SegmentVariantSelector = ({
    tripId,
    entityId,
    referenceType,
    onSave,
    onCancel,
    loading: externalLoading = false
}) => {
    const { t, i18n } = useTranslation();
    const { background, text, border, components, semantic, cn } = useTheme();
    const { distanceUnit } = useDateTimePreferences();
    const durationLanguage = i18n?.resolvedLanguage || i18n?.language;

    // State
    const [segments, setSegments] = useState([]);
    const [existingVariants, setExistingVariants] = useState({});
    const [mergeableGroups, setMergeableGroups] = useState([]);
    const [routeId, setRouteId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [deletingVariant, setDeletingVariant] = useState(null);
    const [isDeletingVariant, setIsDeletingVariant] = useState(false);
    const [pendingSaveSegments, setPendingSaveSegments] = useState(null);

    // Selection state: Map of segmentKey -> { selected: boolean, variantName: string }
    const [selections, setSelections] = useState({});

    // Merge state: Set of merged group indices (which groups are currently merged)
    const [mergedGroupIndices, setMergedGroupIndices] = useState(new Set());

    const fetchSegments = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await segmentMatrixService.getSegmentsForVariant(
                entityId,
                tripId,
                referenceType
            );

            if (response.success) {
                setSegments(response.segments || []);
                setExistingVariants(response.existingVariants || {});
                setMergeableGroups(response.mergeableGroups || []);
                setRouteId(response.routeId);

                // Pre-select segments with "different" deviation status
                const initialSelections = {};
                for (const seg of response.segments || []) {
                    const key = buildSegmentKey(seg.fromWaypointId, seg.toWaypointId);
                    const isDifferent = seg.deviation?.status === 'different';
                    initialSelections[key] = {
                        selected: isDifferent,
                        variantName: ''
                    };
                }
                setSelections(initialSelections);
            }
        } catch (err) {
            console.error('Error fetching segments:', err);
            setError(err.response?.data?.error || t('failedToLoadSegments'));
        } finally {
            setIsLoading(false);
        }
    }, [entityId, referenceType, t, tripId]);

    // Fetch segments on mount
    useEffect(() => {
        fetchSegments();
    }, [fetchSegments]);

    /**
     * Build the display list: either show individual segments or merged groups
     * depending on which groups the user has merged.
     */
    const displaySegments = useMemo(() => {
        if (mergedGroupIndices.size === 0) return segments.map((s, i) => ({ ...s, _index: i, _isMerged: false }));

        // Build set of segment indices that are part of merged groups
        const mergedIndices = new Set();
        const mergedRows = [];
        for (const groupIdx of mergedGroupIndices) {
            const group = mergeableGroups[groupIdx];
            if (!group) continue;
            for (const idx of group.componentSegmentIndices) {
                mergedIndices.add(idx);
            }
            // Create a merged segment row
            mergedRows.push({
                fromWaypointId: group.fromWaypointId,
                toWaypointId: group.toWaypointId,
                fromName: group.fromName,
                toName: group.toName,
                intermediateWaypoints: group.intermediateWaypoints,
                _isMerged: true,
                _groupIdx: groupIdx,
                _insertAfter: group.componentSegmentIndices[0],
                // Combine metrics from component segments
                tripDuration: group.componentSegmentIndices.reduce((sum, idx) =>
                    sum + (getSegmentDisplayDurationSeconds(segments[idx]) || 0), 0),
                refDistance: group.componentSegmentIndices.reduce((sum, idx) =>
                    sum + (segments[idx]?.refDistance || 0), 0),
                deviation: null // No single deviation for merged groups
            });
        }

        // Build display list: individual segments + merged rows in order
        const result = [];
        let mergedRowIdx = 0;
        const sortedMergedRows = mergedRows.sort((a, b) => a._insertAfter - b._insertAfter);

        for (let i = 0; i < segments.length; i++) {
            // Check if we should insert a merged row here
            while (mergedRowIdx < sortedMergedRows.length && sortedMergedRows[mergedRowIdx]._insertAfter === i) {
                result.push(sortedMergedRows[mergedRowIdx]);
                mergedRowIdx++;
            }
            // Skip individual segments that are part of a merged group
            if (!mergedIndices.has(i)) {
                result.push({ ...segments[i], _index: i, _isMerged: false });
            }
        }
        return result;
    }, [segments, mergedGroupIndices, mergeableGroups]);

    const handleToggleMerge = (groupIdx) => {
        const group = mergeableGroups[groupIdx];
        if (!group) return;

        setMergedGroupIndices(prev => {
            const next = new Set(prev);
            if (next.has(groupIdx)) {
                next.delete(groupIdx);
                // Deselect the merged key when unmerging
                const mergedKey = buildSegmentKey(group.fromWaypointId, group.toWaypointId);
                setSelections(s => {
                    const copy = { ...s };
                    delete copy[mergedKey];
                    return copy;
                });
            } else {
                const selectedComponents = new Set(group.componentSegmentIndices || []);
                const keysToClear = [];
                for (const existingIdx of Array.from(next)) {
                    const existingGroup = mergeableGroups[existingIdx];
                    const overlaps = (existingGroup?.componentSegmentIndices || [])
                        .some(idx => selectedComponents.has(idx));
                    if (overlaps) {
                        next.delete(existingIdx);
                        keysToClear.push(buildSegmentKey(existingGroup.fromWaypointId, existingGroup.toWaypointId));
                    }
                }
                if (keysToClear.length > 0) {
                    setSelections(s => {
                        const copy = { ...s };
                        for (const key of keysToClear) {
                            delete copy[key];
                        }
                        return copy;
                    });
                }
                next.add(groupIdx);
            }
            return next;
        });
    };

    const handleToggleSegment = (segmentKey) => {
        setSelections(prev => ({
            ...prev,
            [segmentKey]: {
                ...prev[segmentKey],
                selected: !prev[segmentKey]?.selected
            }
        }));
    };

    const handleVariantNameChange = (segmentKey, name) => {
        setSelections(prev => ({
            ...prev,
            [segmentKey]: {
                ...prev[segmentKey],
                variantName: sanitizeVariantName(name)
            }
        }));
    };

    const getSelectedSegments = () => {
        return displaySegments
            .filter(seg => {
                const key = buildSegmentKey(seg.fromWaypointId, seg.toWaypointId);
                return selections[key]?.selected;
            })
            .map(seg => {
                const key = buildSegmentKey(seg.fromWaypointId, seg.toWaypointId);
                return {
                    fromWaypointId: seg.fromWaypointId,
                    toWaypointId: seg.toWaypointId,
                    variantName: selections[key]?.variantName || ''
                };
            });
    };

    const validateSelections = () => {
        const selected = getSelectedSegments();

        if (selected.length === 0) {
            return { valid: false, error: t('selectAtLeastOneSegment') };
        }

        for (const seg of selected) {
            if (!seg.variantName) {
                return {
                    valid: false,
                    error: t('variantNameRequired')
                };
            }
            if (seg.variantName === 'standard') {
                return {
                    valid: false,
                    error: t('standardReserved')
                };
            }
            if (!isValidVariantName(seg.variantName)) {
                return {
                    valid: false,
                    error: t('invalidVariantName')
                };
            }
        }

        return { valid: true };
    };

    const getReplacementCountForSegments = (segmentsToCheck) => (
        segmentsToCheck.filter(seg => {
            const key = buildSegmentKey(seg.fromWaypointId, seg.toWaypointId);
            return (existingVariants[key] || []).includes(seg.variantName);
        }).length
    );

    const handleSaveClick = () => {
        const validation = validateSelections();
        if (!validation.valid) {
            toast.error(validation.error);
            return;
        }

        setPendingSaveSegments(getSelectedSegments());
    };

    const handleSave = async () => {
        if (!pendingSaveSegments?.length) {
            return;
        }

        try {
            setIsSaving(true);

            const response = await segmentMatrixService.saveSegmentVariants(
                entityId,
                tripId,
                referenceType,
                pendingSaveSegments
            );

            if (response.success) {
                const savedCount = response.saved?.length || response.savedSegments?.length || 0;
                toast.success(
                    t('segmentVariantsSaved', { count: savedCount })
                );

                if (onSave) {
                    onSave(response);
                }
            }
        } catch (err) {
            console.error('Error saving segment variants:', err);
            toast.error(err.response?.data?.error || t('failedToSaveVariants'));
        } finally {
            setPendingSaveSegments(null);
            setIsSaving(false);
        }
    };

    const selectedCount = Object.values(selections).filter(s => s?.selected).length;
    const isDisabled = externalLoading || isSaving;
    const pendingReplacementCount = pendingSaveSegments ? getReplacementCountForSegments(pendingSaveSegments) : 0;

    const handleDeleteExistingVariant = (segment, variant) => {
        if (!routeId) {
            return;
        }
        setDeletingVariant({
            fromWaypointId: segment.fromWaypointId,
            toWaypointId: segment.toWaypointId,
            fromName: segment.fromName,
            toName: segment.toName,
            variant
        });
    };

    const confirmDeleteExistingVariant = async () => {
        if (!deletingVariant || !routeId) {
            return;
        }

        try {
            setIsDeletingVariant(true);
            await segmentMatrixService.deleteSegment(
                entityId,
                routeId,
                deletingVariant.fromWaypointId,
                deletingVariant.toWaypointId,
                referenceType,
                deletingVariant.variant,
                false,
                false
            );

            const key = buildSegmentKey(deletingVariant.fromWaypointId, deletingVariant.toWaypointId);
            setExistingVariants(prev => ({
                ...prev,
                [key]: (prev[key] || []).filter(name => name !== deletingVariant.variant)
            }));
            toast.success(t('segmentMatrixDeleteVariantTitle'));
            setDeletingVariant(null);
        } catch (err) {
            toast.error(err.response?.data?.error || t('deleteSegmentFailed'));
        } finally {
            setIsDeletingVariant(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                <p className={cn("mt-3 text-sm", text.secondary)}>
                    {t('loadingSegments')}
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cn("p-4 rounded-lg", semantic.error.background, semantic.error.text)}>
                <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    if (segments.length === 0) {
        return (
            <div className={cn("p-4 rounded-lg text-center", semantic.info.background, semantic.info.text)}>
                <Info className="w-8 h-8 mx-auto mb-2" />
                <p>{t('noSegmentsFound')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Instructions */}
            <div className={cn("p-3 rounded-lg flex items-start gap-2", semantic.info.background, semantic.info.text)}>
                <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                    <p className="font-medium mb-1">
                        {t('addVariantSegmentsFor', { type: referenceType })}
                    </p>
                    <p>
                        {t('segmentVariantInstructions')}
                    </p>
                </div>
            </div>

            {/* Mergeable groups info banner */}
            {mergeableGroups.length > 0 && (
                <div className={cn("p-3 rounded-lg flex items-start gap-2", "bg-purple-50 dark:bg-purple-900/20", "text-purple-700 dark:text-purple-400")}>
                    <Merge className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <p className="font-medium mb-1">{t('mergeableGroupsDetected')}</p>
                        {mergeableGroups.map((group, idx) => (
                            <button
                                key={idx}
                                type="button"
                                onClick={() => handleToggleMerge(idx)}
                                disabled={isDisabled}
                                className={cn(
                                    "mt-1 px-2 py-1 rounded text-xs flex items-center gap-1 border",
                                    mergedGroupIndices.has(idx)
                                        ? "bg-purple-200 dark:bg-purple-800 border-purple-300 dark:border-purple-600"
                                        : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                                )}
                            >
                                {mergedGroupIndices.has(idx) ? <Split className="w-3 h-3" /> : <Merge className="w-3 h-3" />}
                                {group.fromName} → {group.toName}
                                <span className="opacity-60">
                                    ({t('mergedVia', { intermediates: group.intermediateWaypoints.map(w => w.name).join(', ') })})
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Segment list */}
            <div className="space-y-2">
                {displaySegments.map((segment) => {
                    const key = buildSegmentKey(segment.fromWaypointId, segment.toWaypointId);
                    const selection = selections[key] || { selected: false, variantName: '' };
                    const segmentExistingVariants = existingVariants[key] || [];

                    return (
                        <SegmentRow
                            key={key}
                            segment={{
                                ...segment,
                                // For merged segments, show a label indicating it's merged
                                fromName: segment._isMerged
                                    ? `${segment.fromName} (${t('spanningVariant')})`
                                    : segment.fromName
                            }}
                            isSelected={selection.selected}
                            variantName={selection.variantName}
                            onToggle={() => handleToggleSegment(key)}
                            onVariantNameChange={(name) => handleVariantNameChange(key, name)}
                            existingVariants={segmentExistingVariants}
                            disabled={isDisabled}
                            onDeleteExistingVariant={(variant) => handleDeleteExistingVariant(segment, variant)}
                            t={t}
                            cn={cn}
                            text={text}
                            border={border}
                            background={background}
                            distanceUnit={distanceUnit}
                            durationLanguage={durationLanguage}
                        />
                    );
                })}
            </div>

            {/* Footer with action buttons */}
            <div className={cn("flex items-center justify-between pt-4 border-t", border.default)}>
                <div className={cn("text-sm", text.secondary)}>
                    {t('segmentsSelected', { count: selectedCount })}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isDisabled}
                        className={cn(
                            "px-4 py-2 rounded",
                            isDisabled ? components.button.disabled : components.button.secondary
                        )}
                    >
                        {t('back')}
                    </button>
                    <button
                        type="button"
                        onClick={handleSaveClick}
                        disabled={isDisabled || selectedCount === 0}
                        className={cn(
                            "px-4 py-2 rounded flex items-center gap-2",
                            isDisabled || selectedCount === 0
                                ? components.button.disabled
                                : components.button.primary
                        )}
                    >
                        {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {t('saveVariants')}
                    </button>
                </div>
            </div>

            <ConfirmDialogShell
                isOpen={!!pendingSaveSegments}
                onClose={() => !isSaving && setPendingSaveSegments(null)}
                onConfirm={handleSave}
                title={t('confirmSaveSegmentVariants') || 'Confirm Segment Variant Save'}
                confirmLabel={t('saveVariants')}
                confirmVariant={pendingReplacementCount > 0 ? 'warning' : 'primary'}
                confirmIcon={pendingReplacementCount > 0 ? AlertTriangle : Check}
                isLoading={isSaving}
                isDisabled={isSaving}
                showIcon={pendingReplacementCount > 0}
                iconVariant="warning"
                closeOnBackdropClick={!isSaving}
            >
                <div className="space-y-3">
                    <p className={cn("text-sm", text.secondary)}>
                        {t('confirmSaveSegmentVariantsMessage', {
                            count: pendingSaveSegments?.length || 0,
                            type: referenceType
                        }) || `Save ${pendingSaveSegments?.length || 0} segment variant(s) to ${referenceType}?`}
                    </p>
                    {pendingReplacementCount > 0 && (
                        <div className={cn("p-3 rounded-lg text-sm", semantic.warning.background, semantic.warning.text)}>
                            {t('segmentVariantsReplaceWarning', { count: pendingReplacementCount }) ||
                                `${pendingReplacementCount} selected variant(s) already exist and will be replaced.`}
                        </div>
                    )}
                </div>
            </ConfirmDialogShell>

            <ConfirmDialogShell
                isOpen={!!deletingVariant}
                onClose={() => !isDeletingVariant && setDeletingVariant(null)}
                onConfirm={confirmDeleteExistingVariant}
                title={t('segmentMatrixDeleteVariantTitle')}
                confirmLabel={t('delete')}
                confirmVariant="danger"
                showIcon={true}
                iconVariant="danger"
                isDisabled={isDeletingVariant}
                isLoading={isDeletingVariant}
            >
                <div className={cn("text-center", text.secondary)}>
                    {deletingVariant && (
                        <p>
                            {t('segmentMatrixDeleteVariantConfirm', {
                                name: deletingVariant.variant,
                                from: deletingVariant.fromName,
                                to: deletingVariant.toName
                            })}
                        </p>
                    )}
                </div>
            </ConfirmDialogShell>
        </div>
    );
};

export default SegmentVariantSelector;
